import { LitElement, html } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { unsafeHTML } from 'lit/directives/unsafe-html.js';
import { cache } from 'lit/directives/cache.js';
import { FormScanner, type FieldDescriptor } from './FormScanner.js';
import { LLMClient, type LLMResponse, type MultiFormatValue } from './LLMClient.ts';
// import { applyValuesGlobal } from './applyValues.js'; // Не используется - используем applyValuesToSingleForm
import { icons } from './constants/icons.js';
import { CONFIG } from './constants/config.ts';
import { aiFormCopilotStyles } from './styles/ai-form-copilot.styles.ts';
import { TranslationService } from './services/TranslationService.ts';
import { BrowserUtils } from './utils/browser.ts';
// import { LanguageUtils } from './utils/language.ts'; // Removed per "GPT решает всё" philosophy
import type { AssistantStep } from './types';
import { AudioWorkletRecorder, AudioRecorder } from './AudioRecorder.ts';
import { AudioQualityDetector } from './AudioQualityDetector.ts';
import { BrowserTestUtil } from './BrowserTestUtil.ts';


@customElement('ai-form-copilot')
export class AiFormCopilot extends LitElement {
  
  protected willUpdate(changedProps: Map<string, any>): void {
    // console.log('[PERF] willUpdate called with props:', Array.from(changedProps.keys()));
    super.willUpdate(changedProps);
  }
  static styles = aiFormCopilotStyles;

  private static readonly WIDGET_VERSION = "v2.103"; // Store element refs for generated field names
  
  // TODO: ВРЕМЕННО! Поставить false после отладки модалки успеха
  private DEBUG_SKIP_TO_SUCCESS = false;
  
  // Позиционирование виджета
  private widgetPosition: { bottom: number; right?: number; left?: number } = { bottom: 20, right: 20 };
  private smartZIndex = { widget: 999999, modal: 1000000 };
  private userConfigApplied = false; // Флаг применения пользовательской конфигурации
  private autoPositionDisabled = false; // Флаг отключения автоматического позиционирования
  private showDelay = 1500; // Задержка показа виджета по умолчанию (2 секунды)
  private initialShowTimer: NodeJS.Timeout | null = null;
  private positionCheckTimer: NodeJS.Timeout | null = null;
  private positionCheckCount = 0;

  constructor() {
    super();
    console.log(`🚀 [AI Form Copilot ${AiFormCopilot.WIDGET_VERSION}] Widget initialized - Universal form detection`);
    console.log('[AI Copilot] OpenAI API Key check:', import.meta.env.VITE_OPENAI_API_KEY ? 'Present' : 'Missing');
    console.log('[AI Copilot] Build timestamp:', new Date().toISOString());
  }

  @state() private showModal = false;
  @state() private currentStep: AssistantStep = 'initial';
  // @ts-ignore TS6133 - Used by Lit's reactivity system
  @state() private _transcribedText = '';
  // @ts-ignore TS6133 - Used by Lit's reactivity system
  @state() private _isRecording = false;
  @state() private fieldsDetected = 0;
  @state() private isLoading = false;
  @state() private errorMessage = '';
  @state() private errorDetails = '';
  @state() private lastLLMResponse: LLMResponse | null = null;
  @state() private focusedForm: HTMLFormElement | null = null;
  @state() private scannedFields: FieldDescriptor[] = [];
  @state() private hideWidget = false;
  @state() private currentLang = CONFIG.DEFAULT_LANG;
  @state() private highlightedForm: HTMLFormElement | null = null;
  @state() private cachedSafeBadges: any = null;
  @state() private isLoadingBadges = false;
  @state() private expandedSections: { empty: boolean; check: boolean; filled: boolean } = {
    empty: false,
    check: false,
    filled: false
  };
  
  // Кэшируем результаты для оптимизации
  private _cachedFieldStatuses: { filled: any[], needsCheck: any[], empty: any[] } | null = null;
  private _lastLLMResponseForCache: string = ''; // 🛡️ Строковый кэш-ключ вместо объекта

  private llmClient = new LLMClient();
  private translationService = new TranslationService(import.meta.env.VITE_OPENAI_API_KEY);
  private globalFocusHandler: ((_: FocusEvent) => void) | null = null;
  private audioRecorder: AudioWorkletRecorder | AudioRecorder | null = null;
  private mediaRecorder: MediaRecorder | null = null;
  private pendingGenerateSafeBadges: Promise<any> | null = null; // Global deduplication
  private lastProcessedFieldsHash: string = ''; // Track last processed fields hash
  private languageCache: Map<string, string> = new Map(); // Cache language by form hash
  private safeBadgesCache: Map<string, any> = new Map(); // Cache safe badges results by form hash
  private fieldElementsMap: Map<string, HTMLElement> = new Map(); // Map fallback field names to DOM elements
  private audioChunks: Blob[] = [];
  @state() private isMicrophonePermissionGranted: boolean | null = null;
  private mutationObserver: MutationObserver | null = null;
  @state() private detectedLanguage: string = CONFIG.DEFAULT_LANG;
  @state() private originalUserText: Record<string, string> = {};
  @state() private rawUserInput: string = '';

  private scrollHandler: (() => void) | null = null;
  private resizeHandler: (() => void) | null = null;
  private languageDetectionTimeout: NodeJS.Timeout | null = null;
  private isDetectingLanguage: boolean = false;
  private safeFieldNames: string[] = []; // Список имен безопасных полей для заполнения

  private isNonLinguisticValue(originalText: string, translatedValue: string): boolean {
    // Проверка что это ТОЛЬКО число (без текста)
    const pureNumberPattern = /^\d+([.,]\d+)?$/;
    if (pureNumberPattern.test(originalText.trim()) && pureNumberPattern.test(translatedValue.trim())) {
      const originalNum = originalText.replace(/[^\d]/g, '');
      const translatedNum = translatedValue.replace(/[^\d]/g, '');
      if (originalNum === translatedNum) {
        return true;
      }
    }

    // Даты в формате YYYY-MM-DD
    const datePattern = /^\d{4}-\d{2}-\d{2}$/;
    if (datePattern.test(translatedValue)) {
      return true;
    }

    // Email адреса
    const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (emailPattern.test(translatedValue) && emailPattern.test(originalText)) {
      const originalDomain = originalText.split('@')[1];
      const translatedDomain = translatedValue.split('@')[1];
      if (originalDomain === translatedDomain) {
        return true;
      }
    }

    // Телефонные номера (только цифры совпадают)
    const originalPhone = originalText.replace(/[^\d]/g, '');
    const translatedPhone = translatedValue.replace(/[^\d]/g, '');
    if (originalPhone && originalPhone === translatedPhone && originalPhone.length >= 7) {
      return true;
    }

    // Коды и ID (буквы и цифры)
    const codePattern = /^[A-Za-z0-9\-_]+$/;
    if (codePattern.test(originalText) && codePattern.test(translatedValue) && originalText === translatedValue) {
      return true;
    }

    return false;
  }

  async connectedCallback() {
    super.connectedCallback();
    
    // Скрываем виджет изначально
    this.hideWidget = true;
    
    await this.initializeI18n();
    
    this.injectGlobalStyles();

    // Убираем дублирующую строку - globalFocusHandler определяется ниже

    this.scrollHandler = this.throttle(() => {
      // Только если виджет открыт
      if (this.showModal) {
        this.updateActiveForm();
      }
    }, 500);
    
    // Resize handler для обновления позиции виджета
    this.resizeHandler = this.throttle(() => {
      // Проверяем конфигурацию только если она еще не была применена
      if (!this.userConfigApplied) {
        this.checkUserConfiguration();
      }
      // Если автопозиционирование не отключено, используем его
      if (!this.autoPositionDisabled) {
        this.findSafePosition();
      }
      this.requestUpdate();
    }, 500);

    window.addEventListener('scroll', this.scrollHandler, { passive: true });
    window.addEventListener('resize', this.resizeHandler, { passive: true });

    this.mutationObserver = new MutationObserver(this.throttle(() => {
      // Don't update forms during modal interactions to prevent lag
      if (!this.showModal) {
        this.scanAllForms();
        this.updateActiveForm();
      }
    }, 1000));
    this.mutationObserver.observe(document.body, { childList: true, subtree: true });
    
    // Сначала проверяем пользовательскую конфигурацию
    this.checkUserConfiguration();
    
    // Если автопозиционирование не отключено, используем его
    if (!this.autoPositionDisabled) {
      this.positionCheckCount = 0; // Сбрасываем счетчик для правильного логирования
      this.findSafePosition();
    }
    
    // Определяем умный z-index
    this.calculateSmartZIndex();

    this.globalFocusHandler = (event: FocusEvent) => {
      const target = event.target as HTMLElement;
      if (target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.tagName === 'SELECT')) {
        const form = target.closest('form') as HTMLFormElement;
        if (form && form !== this.focusedForm) {
          this.focusedForm = form;
          this.updateActiveForm();
          this.updateFormHighlight();
        }
      } else {
        if (this.focusedForm) {
          this.focusedForm = null;
          this.updateActiveForm();
          this.updateFormHighlight();
        }
      }
    };
    document.addEventListener('focusin', this.globalFocusHandler, true);
    
    // Сканируем формы для начальной проверки
    this.scanAllForms();
    
    // Проверяем также наличие input полей (включая кастомные компоненты)
    const nativeInputs = document.querySelectorAll('input:not([type="hidden"]), textarea, select').length;
    const customInputs = Array.from(document.querySelectorAll('*')).filter(el => {
      const tagName = el.tagName.toLowerCase();
      return (tagName.includes('input') || 
              tagName.includes('select') || 
              tagName.includes('textarea') ||
              tagName.includes('search') ||
              tagName.includes('picker')) &&
             !['input', 'select', 'textarea'].includes(tagName);
    }).length;
    
    let hasInputFields = nativeInputs > 0 || customInputs > 0;
    console.log(`[AI Copilot] Field detection - Native: ${nativeInputs}, Custom: ${customInputs}`);
    console.log(`[AI Copilot] Initial check - hideWidget: ${this.hideWidget}, fieldsDetected: ${this.fieldsDetected}, hasInputFields: ${hasInputFields}`);
    
    // Функция для запуска таймера показа виджета
    const startShowTimer = () => {
      if (this.initialShowTimer) return; // Уже запущен
      
      console.log(`[AI Copilot] Forms/fields detected (forms: ${this.fieldsDetected}, hasInputs: true). Widget will show in ${this.showDelay}ms`);
      this.initialShowTimer = setTimeout(() => {
        this.hideWidget = false;
        console.log('[AI Copilot] Widget shown after delay, hideWidget:', this.hideWidget);
        this.requestUpdate(); // Принудительно обновляем отображение
        
        // Запускаем периодическую проверку позиции после показа виджета
        if (!this.autoPositionDisabled) {
          this.startPositionChecking();
        }
      }, this.showDelay);
    };
    
    // Запускаем таймер для показа виджета с задержкой
    if (this.fieldsDetected > 0 || hasInputFields) {
      startShowTimer();
      
      // Если пользователь сразу фокусируется на форме, показываем виджет немедленно
      const immediateShowHandler = (e: FocusEvent) => {
        const target = e.target as HTMLElement;
        if (target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.tagName === 'SELECT')) {
          const form = target.closest('form');
          if (form) {
            if (this.initialShowTimer) {
              clearTimeout(this.initialShowTimer);
              this.initialShowTimer = null;
            }
            this.hideWidget = false;
            console.log('[AI Copilot] Widget shown immediately due to form focus');
            document.removeEventListener('focusin', immediateShowHandler, true);
          }
        }
      };
      document.addEventListener('focusin', immediateShowHandler, true);
    } else {
      console.log('[AI Copilot] No forms or input fields detected initially, will check again in 1 second');
      
      // Для динамически загружаемых форм - проверяем еще раз через секунду
      setTimeout(() => {
        // Сканируем снова
        this.scanAllForms();
        
        // Проверяем наличие полей включая кастомные компоненты
        const delayedNativeInputs = document.querySelectorAll('input:not([type="hidden"]), textarea, select').length;
        const delayedCustomInputs = Array.from(document.querySelectorAll('*')).filter(el => {
          const tagName = el.tagName.toLowerCase();
          return (tagName.includes('input') || 
                  tagName.includes('select') || 
                  tagName.includes('textarea') ||
                  tagName.includes('search') ||
                  tagName.includes('picker')) &&
                 !['input', 'select', 'textarea'].includes(tagName);
        }).length;
        const delayedHasInputFields = delayedNativeInputs > 0 || delayedCustomInputs > 0;
        
        console.log(`[AI Copilot] Delayed check - fieldsDetected: ${this.fieldsDetected}, native: ${delayedNativeInputs}, custom: ${delayedCustomInputs}`);
        
        if (this.fieldsDetected > 0 || delayedHasInputFields) {
          startShowTimer();
        } else {
          console.log('[AI Copilot] Still no forms or input fields detected, widget remains hidden');
        }
      }, 1000);
    }
    
    // Также слушаем появление input полей через MutationObserver
    const formObserver = new MutationObserver(() => {
      if (!this.initialShowTimer && this.hideWidget) {
        const currentHasInputFields = document.querySelectorAll('input, textarea, select').length > 0;
        if (currentHasInputFields) {
          console.log('[AI Copilot] Input fields detected by MutationObserver');
          startShowTimer();
          formObserver.disconnect(); // Останавливаем наблюдение после запуска таймера
        }
      }
    });
    
    // Наблюдаем за изменениями в body
    formObserver.observe(document.body, { 
      childList: true, 
      subtree: true,
      attributes: false,
      characterData: false
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    
    // Очищаем таймер показа виджета
    if (this.initialShowTimer) {
      clearTimeout(this.initialShowTimer);
      this.initialShowTimer = null;
    }
    
    // Останавливаем проверку позиции
    this.stopPositionChecking();
    
    this.safeBadgesCache.clear();
    this.languageCache.clear();
    this.fieldElementsMap.clear();

    if (this.globalFocusHandler) {
      document.removeEventListener('focusin', this.globalFocusHandler, true);
    }
    if (this.scrollHandler) {
      window.removeEventListener('scroll', this.scrollHandler);
    }
    if (this.resizeHandler) {
      window.removeEventListener('resize', this.resizeHandler);
    }
    if (this.focusedForm) {
      this.focusedForm.classList.remove('ai-form-active-glow');
    }
    if (this.audioRecorder) {
      this.audioRecorder.stopRecording();
      this.audioRecorder = null;
    }
    if (this.mediaRecorder && this.mediaRecorder.state !== 'inactive') {
      this.mediaRecorder.stop();
    }
    if (this.mutationObserver) {
      this.mutationObserver.disconnect();
    }
    
    this.clearFormHighlight();
    
    const styleElement = document.getElementById('ai-copilot-form-styles');
    if (styleElement) {
      styleElement.remove();
    }
  }

  private throttle(func: Function, limit: number): () => void {
    let inThrottle: boolean;
    return function(this: any, ...args: any[]) {
      if (!inThrottle) {
        func.apply(this, args);
        inThrottle = true;
        setTimeout(() => inThrottle = false, limit);
      }
    };
  }

  private debouncedLanguageDetection() {
    if (this.isDetectingLanguage) {
      return;
    }
    
    if (this.languageDetectionTimeout) {
      clearTimeout(this.languageDetectionTimeout);
    }
    
    this.languageDetectionTimeout = setTimeout(async () => {
      if (this.isDetectingLanguage) return; // Двойная проверка
      
      this.isDetectingLanguage = true;
      try {
        const newLanguage = await this.getFormLanguage();
        if (newLanguage !== this.detectedLanguage) {
          console.log('[AI Copilot] Form language changed from', this.detectedLanguage, 'to', newLanguage);
          this.detectedLanguage = newLanguage;
          this.requestUpdate();
        }
      } finally {
        this.isDetectingLanguage = false;
      }
    }, 1000);
  }

  private async updateActiveForm() {
    this.updateFormHighlight();
    
    // Регенерируем бэджи при смене активной формы
    if (this.showModal && (this.currentStep === 'initial' || this.currentStep === 'listening')) {
      const activeForm = this.selectActiveForm();
      if (activeForm) {
        // Сканируем поля активной формы
        const formScanner = new FormScanner(activeForm);
        const fields = formScanner.scan();
        
        // Проверяем, изменились ли поля
        const currentFieldsHash = JSON.stringify(fields.map(f => ({ name: f.name, type: f.type, label: f.label })));
        if (currentFieldsHash !== this.lastProcessedFieldsHash) {
          this.lastProcessedFieldsHash = currentFieldsHash;
          this.scannedFields = fields;
          
          // Генерируем новые бэджи только если не показываем тултип
          if (fields.length > 0) {
            console.log('[AI Copilot] Regenerating badges for new active form...');
            this.isLoadingBadges = true;
            this.cachedSafeBadges = await this.generateSafeBadges(fields);
            this.isLoadingBadges = false;
          }
        }
      }
    }
  }


  private checkUserConfiguration() {
    // Проверяем глобальную конфигурацию виджета
    const config = (window as any).aiFormCopilotConfig;
    console.log('[AI Copilot] Checking user configuration. window.aiFormCopilotConfig:', config);
    
    if (config && typeof config === 'object') {
      console.log('[AI Copilot] Found user configuration:', config);
      
      // Применяем позиционирование из конфигурации
      if (config.position) {
        const { bottom, top, left, right } = config.position;
        
        // Устанавливаем CSS переменные для позиционирования
        const style = this.style;
        
        if (typeof bottom === 'number') {
          style.setProperty('--widget-bottom', `${bottom}px`);
          this.widgetPosition.bottom = bottom;
        } else if (typeof top === 'number') {
          style.setProperty('--widget-bottom', 'auto');
          style.setProperty('--widget-top', `${top}px`);
        }
        
        if (typeof right === 'number') {
          style.setProperty('--widget-right', `${right}px`);
          style.setProperty('--widget-left', 'auto');
          this.widgetPosition = { ...this.widgetPosition, right, left: undefined };
        } else if (typeof left === 'number') {
          style.setProperty('--widget-left', `${left}px`);
          style.setProperty('--widget-right', 'auto');
          this.widgetPosition = { ...this.widgetPosition, left, right: undefined };
        }
        
        this.userConfigApplied = true;
        console.log('[AI Copilot] Applied user position config:', config.position);
      }
      
      // Применяем z-index из конфигурации
      if (config.zIndex) {
        const zIndex = parseInt(config.zIndex);
        if (!isNaN(zIndex)) {
          this.style.setProperty('--widget-z-index', zIndex.toString());
          this.smartZIndex.widget = zIndex;
          this.smartZIndex.modal = zIndex + 1;
          console.log('[AI Copilot] Applied user z-index:', zIndex);
        }
      }
      
      // Применяем отключение автоматического позиционирования
      if (config.disableAutoPosition === true) {
        this.autoPositionDisabled = true;
        console.log('[AI Copilot] Automatic positioning disabled by user config');
      }
      
      // Применяем задержку показа виджета
      if (config.showDelay !== undefined && typeof config.showDelay === 'number') {
        this.showDelay = Math.max(0, config.showDelay); // Минимум 0
        console.log('[AI Copilot] Custom show delay:', this.showDelay + 'ms');
      }
    } else {
      console.log('[AI Copilot] No user configuration found, using defaults');
    }
  }

  private findSafePosition() {
    // Логируем только при первом вызове или когда есть изменения
    const widgetSize = { width: 80, height: 80 }; // Размер нашей кнопки
    const positions = [
      { bottom: 20, right: 20 },     // Стандартная позиция
      { bottom: 120, right: 20 },    // Сдвинуто выше на 100px
      { bottom: 20, left: 20 },      // Левый нижний угол
      { bottom: 120, left: 20 },     // Левый нижний угол, выше
    ];
    
    const previousPosition = JSON.stringify(this.widgetPosition);
    
    for (const pos of positions) {
      if (!this.hasConflictAtPosition(pos, widgetSize)) {
        this.widgetPosition = pos;
        
        // Если позиция изменилась, обновляем отображение и логируем
        if (JSON.stringify(this.widgetPosition) !== previousPosition) {
          console.log(`[AI Copilot] Widget position changed to:`, pos);
          this.requestUpdate();
        }
        return;
      }
    }
    
    // Если все позиции заняты, используем дефолтную с большим отступом
    this.widgetPosition = { bottom: 180, right: 20 };
    console.log(`[AI Copilot] All positions occupied, using fallback:`, this.widgetPosition);
    
    if (JSON.stringify(this.widgetPosition) !== previousPosition) {
      this.requestUpdate();
    }
  }
  
  private startPositionChecking() {
    // Проверяем позицию каждые 500мс в течение первых 10 секунд
    this.positionCheckTimer = setInterval(() => {
      this.positionCheckCount++;
      
      if (this.positionCheckCount > 20 || this.autoPositionDisabled) { // 20 * 500ms = 10 секунд
        this.stopPositionChecking();
        return;
      }
      
      // Проверяем, не появились ли новые виджеты
      this.findSafePosition();
    }, 500);
  }
  
  private stopPositionChecking() {
    if (this.positionCheckTimer) {
      clearInterval(this.positionCheckTimer);
      this.positionCheckTimer = null;
      console.log('[AI Copilot] Stopped position checking after', this.positionCheckCount, 'checks');
    }
  }
  
  private hasConflictAtPosition(position: any, size: { width: number, height: number }): boolean {
    // Вычисляем границы области где будет находиться наш виджет
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    
    let widgetBounds: { left: number, right: number, top: number, bottom: number };
    
    if ('right' in position) {
      widgetBounds = {
        right: viewportWidth - position.right,
        left: viewportWidth - position.right - size.width,
        bottom: viewportHeight - position.bottom,
        top: viewportHeight - position.bottom - size.height
      };
    } else {
      widgetBounds = {
        left: position.left,
        right: position.left + size.width,
        bottom: viewportHeight - position.bottom,
        top: viewportHeight - position.bottom - size.height
      };
    }
    
    // Сначала ищем все fixed элементы и их детей
    const fixedElements = new Set<Element>();
    const allElements = document.querySelectorAll('*');
    
    // Находим все fixed элементы и их потомков
    for (const el of allElements) {
      const style = window.getComputedStyle(el);
      if (style.position === 'fixed' || style.position === 'sticky') {
        fixedElements.add(el);
        // Добавляем всех потомков fixed элемента
        const descendants = el.querySelectorAll('*');
        descendants.forEach(desc => fixedElements.add(desc));
      }
    }
    
    // Логируем только при первой проверке
    if (this.positionCheckCount === 0) {
      console.log(`[AI Copilot] Found ${fixedElements.size} fixed/sticky elements and their children`);
    }
    
    let hasConflict = false;
    
    for (const el of fixedElements) {
      // Игнорируем наш собственный виджет
      if (el.tagName === 'AI-FORM-COPILOT' || el.closest('ai-form-copilot')) {
        continue;
      }
      
      // Получаем стиль элемента
      const style = window.getComputedStyle(el);
      
      // Проверяем z-index
      const zIndex = parseInt(style.zIndex) || 0;
      const hasHighZIndex = zIndex > 9999;
      
      // Проверяем типичные признаки виджетов
      const tagName = el.tagName.toLowerCase();
      const id = el.id.toLowerCase();
      const className = (el.className || '').toString().toLowerCase();
      const ariaLabel = (el.getAttribute('aria-label') || '').toLowerCase();
      
      // Расширенный список признаков виджетов
      const widgetIndicators = [
        'widget', 'chat', 'fab', 'floating', 'messenger', 'support', 'help',
        'intercom', 'drift', 'zendesk', 'crisp', 'tawk', 'launcher', 'bubble',
        'button', 'toggle', 'trigger', 'livechat', 'feedback', 'survey',
        'message', 'сообщени', 'обмен', 'окно', 'garden' // Zendesk Garden UI
      ];
      
      // Проверяем вложенные элементы (кнопки внутри div и т.д.)
      const hasWidgetButton = el.querySelector('button[aria-label*="сообщени"], button[aria-label*="message"], button[aria-label*="chat"], button[aria-label*="окно"]');
      
      // Проверяем data-атрибуты (часто используются в виджетах)
      const hasWidgetDataAttrs = Array.from(el.attributes).some(attr => 
        attr.name.startsWith('data-') && 
        (attr.name.includes('garden') || attr.name.includes('widget') || 
         attr.name.includes('chat') || attr.name.includes('zendesk'))
      );
      
      const looksLikeWidget = widgetIndicators.some(indicator => 
        tagName.includes(indicator) ||
        id.includes(indicator) ||
        className.includes(indicator) ||
        ariaLabel.includes(indicator)
      ) || tagName === 'iframe' || hasHighZIndex || hasWidgetButton || hasWidgetDataAttrs;
      
      // Получаем границы элемента
      const rect = el.getBoundingClientRect();
      
      // Также проверяем размер - маленькие fixed элементы в углах часто виджеты
      const isSmallCornerElement = rect.width < 100 && rect.height < 100 && 
        (rect.right > viewportWidth - 100 || rect.left < 100) &&
        (rect.bottom > viewportHeight - 100 || rect.top < 100);
      
      if (!looksLikeWidget && !isSmallCornerElement) continue;
      const elBounds = {
        left: rect.left,
        right: rect.right,
        top: rect.top,
        bottom: rect.bottom
      };
      
      // Проверяем пересечение границ
      const overlaps = !(
        widgetBounds.right < elBounds.left ||
        widgetBounds.left > elBounds.right ||
        widgetBounds.bottom < elBounds.top ||
        widgetBounds.top > elBounds.bottom
      );
      
      if (overlaps) {
        console.log(`[AI Copilot] Conflict detected with element:`, {
          element: el,
          tagName: el.tagName,
          id: el.id,
          zIndex: zIndex,
          bounds: elBounds
        });
        hasConflict = true;
        break;
      }
    }
    
    return hasConflict;
  }
  
  private calculateSmartZIndex() {
    let maxZ = 999999;
    
    // Находим максимальный z-index на странице
    const allElements = document.querySelectorAll('*');
    allElements.forEach(el => {
      const style = window.getComputedStyle(el);
      const z = parseInt(style.zIndex);
      
      if (!isNaN(z) && z > maxZ && z < 2147483647) { // Игнорируем максимальное значение
        maxZ = z;
      }
    });
    
    // Устанавливаем наш z-index выше найденного
    this.smartZIndex = {
      widget: maxZ + 1000,
      modal: maxZ + 2000
    };
    
    console.log(`[AI Copilot] Smart z-index calculated: widget=${this.smartZIndex.widget}, modal=${this.smartZIndex.modal}`);
  }

  private injectGlobalStyles() {
    if (document.getElementById('ai-copilot-form-styles')) {
      return;
    }

    const style = document.createElement('style');
    style.id = 'ai-copilot-form-styles';
    style.textContent = `
      .ai-form-active-glow {
        outline: 4px solid rgba(91, 33, 182, 0.6) !important;
        outline-offset: 2px !important;
        border-radius: 12px !important;
        position: relative !important;
        animation: ai-form-pulse-global 2s infinite !important;
        transition: all 0.3s ease !important;
        z-index: 999 !important;
      }

      .ai-form-active-glow::before {
        content: '' !important;
        position: absolute !important;
        top: -6px !important;
        left: -6px !important;
        right: -6px !important;
        bottom: -6px !important;
        background: linear-gradient(45deg, 
          rgba(91, 33, 182, 0.3), 
          rgba(76, 29, 149, 0.3), 
          rgba(91, 33, 182, 0.3)
        ) !important;
        border-radius: 16px !important;
        z-index: -1 !important;
        animation: ai-form-glow-global 3s ease-in-out infinite !important;
        pointer-events: none !important;
      }

      @keyframes ai-form-pulse-global {
        0%, 100% { 
          outline-color: rgba(91, 33, 182, 0.6) !important;
        }
        50% { 
          outline-color: rgba(91, 33, 182, 0.9) !important;
        }
      }

      @keyframes ai-form-glow-global {
        0%, 100% { 
          opacity: 0.3 !important; 
        }
        50% { 
          opacity: 0.6 !important; 
        }
      }
    `;
    
    document.head.appendChild(style);
    console.log('[AI Copilot] Global form highlight styles injected');
  }


  updated(changedProperties: Map<string, any>) {
    // console.log('[PERF] updated called with props:', Array.from(changedProperties.keys()));
    super.updated(changedProperties);
  }

  firstUpdated() {
    this.scanAllForms();

    const activeElement = document.activeElement;
    if (activeElement && activeElement !== document.body && !this.contains(activeElement)) {
      const activeForm = activeElement.closest('form');
      if (activeForm) {
        this.focusedForm = activeForm;
        const scanner = new FormScanner(this.focusedForm);
        this.scannedFields = scanner.scan();
        this.fieldsDetected = this.scannedFields.length;
        
        // Populate fieldElementsMap for fields with saved element references
        this.fieldElementsMap.clear();
        this.scannedFields.forEach(field => {
          if (field.element) {
            this.fieldElementsMap.set(field.name, field.element);
            console.log(`[AI Copilot] Mapped field "${field.name}" to DOM element:`, field.element);
          }
        });
      }
    }
  }

  private async initializeI18n() {
    await this.translationService.initialize();
    this.currentLang = this.translationService.getCurrentLang();
  }


  private t(key: string, params?: Record<string, any>): string {
    return this.translationService.t(key, params);
  }



  private getTotalFormsCount(): number {
    const forms = document.querySelectorAll('form');
    return forms.length;
  }

  private getFormIndex(targetForm: HTMLFormElement): number {
    const allForms = Array.from(document.querySelectorAll('form'));
    return allForms.indexOf(targetForm) + 1;
  }

  private getFormsStatistics() {
    const allForms = Array.from(document.querySelectorAll('form')) as HTMLFormElement[];
    let filledCount = 0;
    let partiallyFilledCount = 0;
    let emptyCount = 0;

    for (const form of allForms) {
      const fillRatio = this.getFormFillRatio(form);
      if (fillRatio >= 0.8) {
        filledCount++;
      } else if (fillRatio > 0) {
        partiallyFilledCount++;
      } else {
        emptyCount++;
      }
    }

    return {
      total: allForms.length,
      filled: filledCount,
      partiallyFilled: partiallyFilledCount,
      empty: emptyCount
    };
  }

  private getFormFillRatio(form: HTMLFormElement): number {
    const inputs = form.querySelectorAll('input, select, textarea');
    if (inputs.length === 0) return 0;

    let filledCount = 0;
    inputs.forEach(input => {
      const element = input as HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement;
      if (element.value && element.value.trim() !== '') {
        filledCount++;
      }
    });

    return filledCount / inputs.length;
  }

  private getElementVisibilityRatio(element: Element): number {
    const rect = element.getBoundingClientRect();
    const windowHeight = window.innerHeight || document.documentElement.clientHeight;
    const windowWidth = window.innerWidth || document.documentElement.clientWidth;
    
    if (rect.width === 0 || rect.height === 0) {
      return 0;
    }
    
    const isVerticallyVisible = rect.top < windowHeight && rect.bottom > 0;
    const isHorizontallyVisible = rect.left < windowWidth && rect.right > 0;
    
    if (!isVerticallyVisible || !isHorizontallyVisible) {
      return 0;
    }
    
    const visibleHeight = Math.min(rect.bottom, windowHeight) - Math.max(rect.top, 0);
    const visibleWidth = Math.min(rect.right, windowWidth) - Math.max(rect.left, 0);
    const elementHeight = rect.bottom - rect.top;
    const elementWidth = rect.right - rect.left;
    
    const heightRatio = Math.max(0, visibleHeight) / elementHeight;
    const widthRatio = Math.max(0, visibleWidth) / elementWidth;
    
    return heightRatio * widthRatio;
  }



  private isFormFilled(form: HTMLFormElement): boolean {
    const inputs = form.querySelectorAll('input, textarea, select');
    const totalFields = inputs.length;
    if (totalFields === 0) return false;

    let filledFields = 0;
    inputs.forEach(input => {
      const element = input as HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement;
      
      if (element.type === 'hidden' || element.disabled ||
          element.type === 'submit' || element.type === 'button') {
        return;
      }

      if (element.type === 'checkbox' || element.type === 'radio') {
        if ((element as HTMLInputElement).checked) filledFields++;
      } else if (element.value && element.value.trim() !== '') {
        filledFields++;
      }
    });

    const fillRatio = filledFields / totalFields;
    return fillRatio > 0.5;
  }

  private selectActiveForm(): HTMLFormElement | null {
    // Приоритет 1: Форма в фокусе
    if (this.focusedForm) {
      return this.focusedForm;
    }

    const allForms = Array.from(document.querySelectorAll('form')) as HTMLFormElement[];
    
    if (allForms.length === 0) {
      return null;
    }

    if (allForms.length === 1) {
      return allForms[0];
    }

    // Приоритет 2: Наиболее видимая незаполненная форма
    let bestForm: HTMLFormElement | null = null;
    let bestVisibility = 0;

    for (const form of allForms) {
      const visibility = this.getElementVisibilityRatio(form);
      
      // Отдаем предпочтение незаполненным формам
      const isFilled = this.isFormFilled(form);
      const adjustedVisibility = isFilled ? visibility * 0.8 : visibility;
      
      if (adjustedVisibility > bestVisibility) {
        bestVisibility = adjustedVisibility;
        bestForm = form;
      }
    }

    // Приоритет 3: Улучшенный fallback - первая незаполненная/частично заполненная
    if (!bestForm || bestVisibility === 0) {
      return this.findBestCandidateForm(allForms);
    }

    return bestForm;
  }

  private findBestCandidateForm(allForms: HTMLFormElement[]): HTMLFormElement | null {
    // Приоритет: незаполненные формы
    const emptyForms = allForms.filter(form => this.getFormFillRatio(form) === 0);
    if (emptyForms.length > 0) return emptyForms[0];
    
    // Затем: частично заполненные формы  
    const partialForms = allForms.filter(form => {
      const ratio = this.getFormFillRatio(form);
      return ratio > 0 && ratio < 0.8;
    });
    if (partialForms.length > 0) return partialForms[0];
    
    // Последний вариант: первая форма
    return allForms[0] || null;
  }

  private getFormTitle(form: HTMLFormElement): string {
    const headings = form.querySelectorAll('h1, h2, h3, h4, h5, h6');
    if (headings.length > 0) {
      return headings[0].textContent?.trim() || '';
    }
    
    // 2. Ищем заголовок перед формой (в предыдущих элементах)
    let element = form.previousElementSibling;
    while (element) {
      if (element.matches('h1, h2, h3, h4, h5, h6')) {
        return element.textContent?.trim() || '';
      }
      element = element.previousElementSibling;
    }
    
    const parent = form.parentElement;
    if (parent) {
      const parentHeadings = parent.querySelectorAll('h1, h2, h3, h4, h5, h6');
      for (const heading of parentHeadings) {
        if (parent.contains(form) && parent.contains(heading)) {
          return heading.textContent?.trim() || '';
        }
      }
    }
    
    if (form.getAttribute('aria-label')) {
      return form.getAttribute('aria-label')!;
    }
    
    if (form.title) {
      return form.title;
    }
    
    const firstLabel = form.querySelector('label, legend');
    if (firstLabel?.textContent?.trim()) {
      return firstLabel.textContent.trim();
    }
    
    return this.t('form.defaultName');
  }


  private generateFieldsHash(fields: any[]): string {
    const fieldsSignature = fields.map(field =>
      `${field.name || ''}-${field.label || ''}-${field.type || ''}-${field.placeholder || ''}`
    ).join('|');
    
    let hash = 0;
    for (let i = 0; i < fieldsSignature.length; i++) {
      const char = fieldsSignature.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash; // Convert to 32bit integer
    }
    return Math.abs(hash).toString(16).substring(0, 8); // Short hex hash
  }

  private async getFormLanguage(): Promise<string> {
    const activeForm = this.selectActiveForm();
    
    let fieldsForAnalysis: any[] = [];
    
    if (activeForm) {
      const scanner = new FormScanner(activeForm);
      fieldsForAnalysis = scanner.scan();
    } else {
      fieldsForAnalysis = FormScanner.scanAllFormsOnPage();
    }
    
    if (fieldsForAnalysis.length === 0) {
      console.log('[AI Copilot] No fields found for analysis, defaulting to en');
      return 'en';
    }
    
    const fieldsHash = this.generateFieldsHash(fieldsForAnalysis);
    const cachedLanguage = this.languageCache.get(fieldsHash);
    if (cachedLanguage) {
      console.log('[AI Copilot] Using cached language detection:', cachedLanguage);
      return cachedLanguage;
    }
    
    try {
      console.log('[AI Copilot] Detecting language for new form:', fieldsHash);
      const detectedLanguage = await this.translationService.detectFormLanguage(fieldsForAnalysis);
      
      this.languageCache.set(fieldsHash, detectedLanguage);
      
      return detectedLanguage;
    } catch (error) {
      console.error('[AI Copilot] Language detection failed, using default:', error);
      // According to "GPT решает всё" philosophy, we don't use hardcoded language detection
      // Return browser language as a sensible default when GPT is unavailable
      const browserLang = navigator.language.split('-')[0];
      const fallbackLanguage = CONFIG.SUPPORTED_LANGS.includes(browserLang) ? browserLang : CONFIG.DEFAULT_LANG;
      
      this.languageCache.set(fieldsHash, fallbackLanguage);
      
      return fallbackLanguage;
    }
  }


  private scanAllForms() {
    const allFields = FormScanner.scanAllFormsOnPage();
    this.fieldsDetected = allFields.length;
    this.scannedFields = allFields;
    
    // Populate fieldElementsMap for fields with saved element references
    this.fieldElementsMap.clear();
    this.scannedFields.forEach(field => {
      if (field.element) {
        this.fieldElementsMap.set(field.name, field.element);
        console.log(`[AI Copilot] Mapped field "${field.name}" to DOM element:`, field.element);
      }
    });
    
    // Если виджет уже был показан, но формы исчезли - скрываем его
    if (!this.hideWidget && allFields.length === 0) {
      // Проверяем также наличие input полей (включая кастомные компоненты)
      const nativeInputs = document.querySelectorAll('input:not([type="hidden"]), textarea, select').length;
      const customInputs = Array.from(document.querySelectorAll('*')).filter(el => {
        const tagName = el.tagName.toLowerCase();
        return (tagName.includes('input') || 
                tagName.includes('select') || 
                tagName.includes('textarea') ||
                tagName.includes('search') ||
                tagName.includes('picker')) &&
               !['input', 'select', 'textarea'].includes(tagName);
      }).length;
      const hasInputFields = nativeInputs > 0 || customInputs > 0;
      if (!hasInputFields) {
        console.log('[AI Copilot] No forms or fields detected, hiding widget');
        this.hideWidget = true;
        // Очищаем таймер, если он еще активен
        if (this.initialShowTimer) {
          clearTimeout(this.initialShowTimer);
          this.initialShowTimer = null;
        }
      }
    }
  }

  private updateFormHighlight() {
    if (this.showModal) {
      // Use requestAnimationFrame to avoid layout thrashing
      requestAnimationFrame(() => {
        const totalForms = this.getTotalFormsCount();
        if (totalForms > 1) {
          const activeForm = this.selectActiveForm();
          
          // Убираем подсветку только если форма изменилась
          if (activeForm !== this.highlightedForm) {
            if (this.highlightedForm) {
              this.highlightedForm.classList.remove('ai-form-active-glow');
            }
            
            if (activeForm) {
              activeForm.classList.add('ai-form-active-glow');
              this.highlightedForm = activeForm;
            }
          } else if (activeForm && !activeForm.classList.contains('ai-form-active-glow')) {
            // Восстанавливаем подсветку, если она была удалена
            activeForm.classList.add('ai-form-active-glow');
          }
        }
      });
    } else {
      // Убираем подсветку только когда модалка закрыта
      if (this.highlightedForm) {
        this.highlightedForm.classList.remove('ai-form-active-glow');
        this.highlightedForm = null;
      }
    }
  }

  private clearFormHighlight() {
    if (this.highlightedForm) {
      this.highlightedForm.classList.remove('ai-form-active-glow');
      this.highlightedForm = null;
    }
  }

  private async handleFloatingButtonClick() {
    this.showModal = true;
    this.updateFormHighlight(); // Включаем подсветку формы при открытии модалки
    this.isLoadingBadges = true; // Включаем лоудер
    
    // TODO: ВРЕМЕННО! Debug режим для быстрого перехода к success модалке
    if (this.DEBUG_SKIP_TO_SUCCESS) {
      console.log('[DEBUG] Skipping to success modal with mock data');
      
      // Создаем моковые данные для тестирования
      const mockFields: FieldDescriptor[] = [
        { name: 'firstName', label: 'Имя', type: 'text', value: 'Иван', required: true },
        { name: 'lastName', label: 'Фамилия', type: 'text', value: 'Иванов', required: true },
        { name: 'email', label: 'Email', type: 'email', value: 'ivan@example.com', required: true },
        { name: 'phone', label: 'Телефон', type: 'tel', value: '+7 (999) 123-45-67', required: false },
        { name: 'birthDate', label: 'Дата рождения', type: 'date', value: '1990-01-01', required: false },
        { name: 'address', label: 'Адрес', type: 'text', value: '', required: false },
        { name: 'city', label: 'Город', type: 'text', value: '', required: true },
        { name: 'postalCode', label: 'Индекс', type: 'text', value: '', required: false },
        { name: 'country', label: 'Страна', type: 'select', value: 'Россия', required: true },
        { name: 'passport', label: 'Паспорт', type: 'text', value: '', required: false, isPrivate: true },
        { name: 'inn', label: 'ИНН', type: 'text', value: '', required: false, isPrivate: true },
        { name: 'comment', label: 'Комментарий', type: 'textarea', value: 'Тестовый комментарий', required: false }
      ];
      
      // Устанавливаем моковые данные
      this.scannedFields = mockFields;
      this.fieldsDetected = mockFields.length;
      
      // Создаем моковый LLM response с оригинальным текстом пользователя
      this.lastLLMResponse = {
        reply: 'Форма успешно заполнена (DEBUG MODE)',
        autofill: {
          firstName: 'Иван',
          lastName: 'Иванов',
          email: 'ivan@example.com',
          phone: '+7 (999) 123-45-67',
          birthDate: '1990-01-01',
          country: 'Россия',
          comment: 'Тестовый комментарий'
        },
        originalText: {
          firstName: 'меня зовут Иван',
          lastName: 'фамилия Иванов',
          email: 'мой имейл ivan@example.com',
          phone: 'телефон 999 123 45 67',
          birthDate: 'родился первого января девяностого года',
          country: 'живу в России',
          comment: 'напиши тестовый комментарий'
        }
      };
      
      // Устанавливаем originalUserText для отображения в UI
      this.originalUserText = this.lastLLMResponse.originalText || {};
      
      // Удалено захардкоженное назначение языков - используем автоматическое определение
      
      // Генерируем badges для полей
      this.cachedSafeBadges = await this.generateSafeBadges(mockFields);
      
      // Устанавливаем safeFieldNames из сгенерированных badges
      if (this.cachedSafeBadges?.safeFields) {
        this.safeFieldNames = this.cachedSafeBadges.safeFields.map((f: any) => f.name);
        console.log('[DEBUG] Safe field names set:', this.safeFieldNames);
      }
      
      // Выключаем лоудер и переходим к success
      this.isLoadingBadges = false;
      this.currentStep = 'success';
      
      return; // Выходим из функции, не выполняя обычную логику
    }
    // TODO: ВРЕМЕННО! Конец debug режима
    
    // Prevent body scroll on mobile
    if (window.innerWidth <= 480) {
      document.body.style.overflow = 'hidden';
      document.body.style.position = 'fixed';
      document.body.style.width = '100%';
      document.body.style.top = `-${window.scrollY}px`;
    }
    
    // Add class to disable transitions during resize
    this.shadowRoot?.querySelector('.modal-overlay')?.classList.add('modal-opened');
    
    console.log('[AI Copilot] Performing API calls on modal open...');
    
    // Определяем активную форму и сканируем только её поля
    const activeForm = this.selectActiveForm();
    if (activeForm) {
      const scanner = new FormScanner(activeForm);
      this.scannedFields = scanner.scan();
      this.fieldsDetected = this.scannedFields.length;
      console.log('[AI Copilot] Scanning active form fields:', this.fieldsDetected);
    } else {
      // Fallback: сканируем все формы только если нет активной
      this.scanAllForms();
      console.log('[AI Copilot] No active form found, scanning all forms:', this.fieldsDetected);
    }
    
    // Populate fieldElementsMap for fields with saved element references
    this.fieldElementsMap.clear();
    this.scannedFields.forEach(field => {
      if (field.element) {
        this.fieldElementsMap.set(field.name, field.element);
        console.log(`[AI Copilot] Mapped field "${field.name}" to DOM element:`, field.element);
      }
    });

    // ВАЖНО: Сначала определяем язык формы, ПОТОМ генерируем badges
    await this.debouncedLanguageDetection();
    
    if (this.scannedFields.length > 0) {
      console.log('[AI Copilot] Pre-generating safe badges for UI display...');
      this.cachedSafeBadges = await this.generateSafeBadges(this.scannedFields);
    }
    
    this.isLoadingBadges = false; // Выключаем лоудер
    
    // Подсвечиваем активную форму при открытии модального окна
    this.updateFormHighlight();
    
    this.resetToInitial();
  }

  private closeModal() {
    this.showModal = false;
    if (this.mediaRecorder && this.mediaRecorder.state !== 'inactive') {
      this.mediaRecorder.stop();
    }
    
    // Restore body scroll on mobile
    if (window.innerWidth <= 480) {
      const scrollY = parseInt(document.body.style.top || '0') * -1;
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.width = '';
      document.body.style.top = '';
      window.scrollTo(0, scrollY);
    }
    
    // Remove class that disables transitions
    this.shadowRoot?.querySelector('.modal-overlay')?.classList.remove('modal-opened');
    
    this.clearFormHighlight();
    
    this.resetToInitial();
  }

  private resetToInitial() {
    this.currentStep = 'initial';
    this.isLoading = false;
    this._transcribedText = '';
    this._isRecording = false; // ИСПРАВЛЕНО: сбрасываем состояние записи
    this.errorMessage = '';
    this.errorDetails = '';
    this.lastLLMResponse = null;
    this.originalUserText = {}; // 🛡️ Очищаем originalUserText
    this.rawUserInput = ''; // 🛡️ Очищаем rawUserInput
    // 🛡️ ИНВАЛИДАЦИЯ КЭША при сбросе
    this._cachedFieldStatuses = null;
    this._lastLLMResponseForCache = '';
    this.expandedSections = {
      empty: false,
      check: false,
      filled: false
    }; // Сбрасываем состояние всех аккордеонов

    if (this.audioRecorder) {
      this.audioRecorder.stopRecording();
      this.audioRecorder = null;
    }
    if (this.mediaRecorder && this.mediaRecorder.state !== 'inactive') {
      this.mediaRecorder.stop();
    }
    this.mediaRecorder = null;
  }

  private createDetailedError(userMessage: string, technicalError: any, context: string): void {
    const deviceInfo = {
      version: AiFormCopilot.WIDGET_VERSION,
      userAgent: navigator.userAgent,
      platform: navigator.platform,
      language: navigator.language,
      cookieEnabled: navigator.cookieEnabled,
      onLine: navigator.onLine,
      timestamp: new Date().toISOString(),
      url: window.location.href,
      context: context
    };

    const errorInfo = {
      message: technicalError?.message || 'Unknown error',
      stack: technicalError?.stack || 'No stack trace',
      name: technicalError?.name || 'Error',
      toString: technicalError?.toString() || 'No string representation'
    };

    this.errorMessage = userMessage;
    this.errorDetails = JSON.stringify({ deviceInfo, errorInfo, originalError: technicalError }, null, 2);

    console.error(`[AI Copilot] Detailed error in ${context}:`, {
      deviceInfo,
      errorInfo,
      originalError: technicalError
    });
  }

  private async requestMicrophonePermission(): Promise<boolean> {
    if (this.isMicrophonePermissionGranted === true) return true;
    if (this.isMicrophonePermissionGranted === false) return false;

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      stream.getTracks().forEach(track => track.stop());
      this.isMicrophonePermissionGranted = true;

      if (BrowserTestUtil.isRealIOSSafari()) {
        const diagnostics = await AudioQualityDetector.runDiagnostics();
        console.log('[AI Copilot] iOS Audio Diagnostics:', diagnostics);
      }

      return true;
    } catch (err) {
      this.isMicrophonePermissionGranted = false;
      this.createDetailedError(this.t('error.microphone'), err, 'microphone_permission');
      this.currentStep = 'error';
      return false;
    }
  }




  private async handleStartListening() {
    this.currentStep = 'listening';

    const hasPermission = await this.requestMicrophonePermission();
    if (!hasPermission) {
      return;
    }

    // API запросы уже выполнены при открытии модального окна

    if (import.meta.env.DEV) {
      BrowserTestUtil.runDiagnostics();
    }

    try {
      const isIOSSafari = BrowserTestUtil.isRealIOSSafari();
      const isIOS = BrowserUtils.getOS() === 'iOS';
      const isMobile = BrowserUtils.isMobile();
      
      console.log(`[AI Copilot v${AiFormCopilot.WIDGET_VERSION}] Audio setup - iOS Safari:`, isIOSSafari, 'iOS:', isIOS, 'Mobile:', isMobile);
      console.log('[AI Copilot] User Agent:', navigator.userAgent);

// ВАЖНО: Для любого iOS всегда используем MediaRecorder
// AudioWorklet на iOS имеет проблемы с захватом звука
      if (isIOS || isIOSSafari) {
        console.log('[AI Copilot] iOS device detected - forcing MediaRecorder');
        await this.fallbackToMediaRecorder();
      } else {
        // ВАЖНО: AudioWorklet и ScriptProcessor НЕ РАБОТАЮТ на iOS для микрофона
        // Всегда используем MediaRecorder для всех платформ
        console.log('[AI Copilot] Using MediaRecorder for all platforms');
        await this.fallbackToMediaRecorder();
      }

    } catch (err) {
      this.createDetailedError(this.t('error.microphone'), err, 'start_recording');
      this.currentStep = 'error';
    }
  }


  private async fallbackToMediaRecorder() {
    const isSafariOrMobile = BrowserUtils.isSafari() || BrowserUtils.isMobile();
    console.log(`[AI Copilot v${AiFormCopilot.WIDGET_VERSION}] Starting fallbackToMediaRecorder`);
    console.log('[AI Copilot] Safari or Mobile detected:', isSafariOrMobile);
    const audioConstraints = isSafariOrMobile
        ? CONFIG.AUDIO.SAFARI_SETTINGS
        : CONFIG.AUDIO.CHROME_SETTINGS;

    console.log('[AI Copilot] Audio constraints:', audioConstraints);
    
    const stream = await navigator.mediaDevices.getUserMedia({
      audio: audioConstraints
    });
    
    console.log('[AI Copilot] Media stream obtained:', {
      active: stream.active,
      tracks: stream.getAudioTracks().length,
      trackSettings: stream.getAudioTracks()[0]?.getSettings()
    });

    let mediaRecorderOptions: MediaRecorderOptions;
    if (isSafariOrMobile) {
      let bestMimeType = 'audio/mp4';
      if (MediaRecorder.isTypeSupported('audio/mp4;codecs=mp4a.40.2')) {
        bestMimeType = 'audio/mp4;codecs=mp4a.40.2';
      } else if (MediaRecorder.isTypeSupported('audio/webm;codecs=opus')) {
        bestMimeType = 'audio/webm;codecs=opus';
      }

      mediaRecorderOptions = {
        mimeType: bestMimeType,
        audioBitsPerSecond: 256000
      };
    } else {
      mediaRecorderOptions = {
        audioBitsPerSecond: CONFIG.AUDIO.CHROME_SETTINGS.audioBitsPerSecond
      };
    }

    this.mediaRecorder = new MediaRecorder(stream, mediaRecorderOptions);
    this.audioChunks = [];

    this.mediaRecorder.ondataavailable = (event) => {
      if (event.data.size > 0) {
        this.audioChunks.push(event.data);
      }
    };

    this.mediaRecorder.onstop = () => {
      // Используем тот же MIME тип, что и MediaRecorder
      const recordedMimeType = this.mediaRecorder?.mimeType || mediaRecorderOptions.mimeType;
      console.log('[AI Copilot] Creating blob with correct MIME type:', recordedMimeType);

      const audioBlob = new Blob(this.audioChunks, { type: recordedMimeType });

      if (audioBlob.size < CONFIG.AUDIO.MIN_BLOB_SIZE) {
        this.errorMessage = 'Recording too short. Please try again.';
        this.currentStep = 'error';
        return;
      }

      console.log('[AI Copilot] Audio blob ready:', {
        size: audioBlob.size,
        sizeKB: (audioBlob.size / 1024).toFixed(2) + 'KB',
        type: audioBlob.type,
        mimeType: recordedMimeType
      });

      this.handleRecordingStop(audioBlob);
      stream.getTracks().forEach(track => track.stop());
    };

    this.mediaRecorder.onerror = (event) => {
      this.createDetailedError('Recording failed. Please try again.', event, 'mediarecorder_error');
      this.currentStep = 'error';
    };

    const timeslice = isSafariOrMobile
        ? CONFIG.AUDIO.SAFARI_SETTINGS.timeslice
        : CONFIG.AUDIO.CHROME_SETTINGS.timeslice;
    this.mediaRecorder.start(timeslice);
    this._isRecording = true;
  }

  private handleStopListening() {
    if (this.audioRecorder) {
      const audioBlob = this.audioRecorder.stopRecording();

      if (audioBlob.size < CONFIG.AUDIO.MIN_BLOB_SIZE) {
        this.errorMessage = 'Recording too short. Please try again.';
        this.currentStep = 'error';
        this.audioRecorder = null;
        return;
      }

      this.handleRecordingStop(audioBlob);
      this.audioRecorder = null;
    } else if (this.mediaRecorder && this.mediaRecorder.state !== 'inactive') {
      this.mediaRecorder.stop();
    }
  }

  private async handleRecordingStop(audioBlob: Blob) {
    this.currentStep = 'processing';
    this.isLoading = true;
    this._isRecording = false; // ИСПРАВЛЕНО: сбрасываем состояние записи
    this.audioChunks = [];

    const formData = new FormData();

    let filename = 'recording.wav';
    if (audioBlob.type.includes('mp4')) {
      filename = 'recording.m4a';
    } else if (audioBlob.type.includes('webm')) {
      filename = 'recording.webm';
    } else if (audioBlob.type.includes('mpeg')) {
      filename = 'recording.mp3';
    }

    formData.append('file', audioBlob, filename);

    // if (this.detectedLanguage) {
    //   formData.append('language', this.detectedLanguage);
    // }

    formData.append('temperature', '0');
    formData.append('response_format', 'verbose_json');

    try {
      console.log('[AI Copilot] Sending transcription request to:', CONFIG.TRANSCRIBE_FUNCTION_URL);
      console.log('[AI Copilot] Environment check - isDev:', import.meta.env.MODE, 'hostname:', window.location.hostname);
      console.log('[AI Copilot] FormData keys:', Array.from(formData.keys()));
      
      const response = await fetch(CONFIG.TRANSCRIBE_FUNCTION_URL, {
        method: 'POST',
        body: formData,
        headers: {
          'Accept': 'application/json'
        }
      });
      
      console.log('[AI Copilot] Transcription response status:', response.status);

      if (!response.ok) {
        const errorText = await response.text();
        console.error('[AI Copilot] Server error:', errorText);
        throw new Error(`Server error: ${response.status} - ${errorText}`);
      }

      const json = await response.json();

      const transcript = json.transcript || json.text;
      const confidence = json.confidence;

      const skipConfidenceCheck = false;

      if (!skipConfidenceCheck) {
        if (CONFIG.AUDIO.CHECK_CONFIDENCE && confidence !== undefined && confidence < CONFIG.AUDIO.MIN_CONFIDENCE) {
          console.warn('[AI Copilot] Low confidence transcription:', confidence);
          
          // Показываем предупреждение пользователю при очень низкой уверенности
          if (confidence < 0.1) { // Менее 10% уверенности
            this.errorMessage = this.t('error.lowConfidence') || 'Could not clearly understand the audio. Please speak more clearly.';
            this.currentStep = 'error';
            this.isLoading = false;
            return;
          }
          
          // При умеренно низкой уверенности продолжаем, но предупреждаем в консоли
          console.log('[AI Copilot] Proceeding with low confidence:', confidence);
        }
      } else {
        console.log('[AI Copilot] Skipping confidence check (temporary debug mode)');
      }

      if (transcript && transcript.trim().length > 0 && !transcript.includes('Amara.org')) {
        await this.sendTextToLLM(transcript);
      } else {
        this.createDetailedError(this.t('error.lowConfidence'),
            { transcript, json }, 'invalid_transcript');
        this.currentStep = 'error';
        this.isLoading = false;
      }
    } catch (err) {
      console.error('[AI Copilot] Transcription failed:', err);
      this.createDetailedError('Transcription failed', err, 'transcription');
      this.currentStep = 'error';
      this.isLoading = false;
    }
  }


  private async sendTextToLLM(text: string) {
    console.log('[AI Copilot] sendTextToLLM called with text:', text.substring(0, 50));
    console.log('[AI Copilot] Detected form language:', this.detectedLanguage);
    if (!text) return;
    
    // Сохраняем оригинальный текст пользователя
    this.rawUserInput = text;
    console.log('[AI Copilot] Saved raw user input:', this.rawUserInput);

    // Определяем поля для заполнения - активная видимая форма
    const activeForm = this.selectActiveForm();
    let fieldsForLLM: any[] = [];

    if (activeForm) {
      const scanner = new FormScanner(activeForm);
      fieldsForLLM = scanner.scan();
      console.log('[AI Copilot] Fields for LLM from active form:', fieldsForLLM.length);
    } else {
      console.log('[AI Copilot] No active form found for LLM processing');
      this.errorMessage = this.t('error.noActiveForm');
      this.currentStep = 'error';
      this.isLoading = false;
      return;
    }

    if (fieldsForLLM.length === 0) {
      this.errorMessage = this.t('error.noFields');
      this.currentStep = 'error';
      this.isLoading = false;
      return;
    }

    // Определяем безопасные поля для заполнения (те же, что отображаются в бэджах)
    const { safeFields } = await this.generateSafeBadges(fieldsForLLM);
    this.safeFieldNames = safeFields.map((f: any) => f.name);
    console.log('[AI Copilot] Safe fields for filling:', this.safeFieldNames);

    console.log('[AI Copilot] Safe fields being sent to LLM:', safeFields.length);
    console.log(`[AI Copilot ${AiFormCopilot.WIDGET_VERSION}] Sending to LLM - User text:`, text, 'Form language:', this.detectedLanguage);
    const response = await this.llmClient.sendPrompt(text, safeFields, this.detectedLanguage);
    this.isLoading = false;
    this.lastLLMResponse = response;

    console.log('[AI Copilot] LLM Response received:', response);

    if (response.error || !response.reply) {
      console.log('[AI Copilot] LLM Error:', response.error);
      this.errorMessage = response.reply || response.error || this.t('error.general');
      this.currentStep = 'error';
    } else {
      console.log('[AI Copilot] LLM Success - autofill data:', response.autofill);
      
      // Используем оригинальные значения, полученные от GPT на бэкенде
      if (response.autofill && response.originalText) {
        this.originalUserText = response.originalText;
        // 🛡️ ПРИНУДИТЕЛЬНАЯ ИНВАЛИДАЦИЯ КЭША при обновлении originalUserText
        this._cachedFieldStatuses = null;
        this._lastLLMResponseForCache = '';
        console.log('[AI Copilot] Original user text from GPT backend:', this.originalUserText);
      }
      
      if (response.autofill && Object.keys(response.autofill).length > 0) {
        // НЕ заполняем форму сразу, а показываем экран предпросмотра
        this.currentStep = 'success';
      } else {
        console.log('[AI Copilot] No autofill data received, showing error');
        this.errorMessage = this.t('error.no_fields_detected');
        this.currentStep = 'error';
      }
    }
  }

  private handleFillForm() {
    if (this.lastLLMResponse?.autofill && Object.keys(this.lastLLMResponse.autofill).length > 0) {
      // СТРОГО используем только активную форму
      const activeForm = this.selectActiveForm();
      
      if (activeForm) {
        console.log('[AI Copilot] Filling ONLY active form:', activeForm);
        // Используем функцию applyValues только для конкретной формы
        this.applyValuesToSingleForm(this.lastLLMResponse.autofill, activeForm);
        // После заполнения закрываем диалог
        this.closeModal();
      } else {
        console.warn('[AI Copilot] No active form found, skipping fill');
        this.errorMessage = 'Не найдена активная форма для заполнения';
        this.currentStep = 'error';
        return;
      }
    } else {
      this.errorMessage = this.t('error.general');
      this.currentStep = 'error';
    }
  }


  private async generateSafeBadges(fieldsForDisplay: any[]) {
    // Generate fields hash for caching using Unicode-safe hashing
    const fieldsSignature = fieldsForDisplay.map((f: any) => 
      `${f.name || ''}-${f.type || ''}-${f.label || ''}-${f.placeholder || ''}`
    ).join('|');
    
    // Simple hash function for Unicode strings
    let hash = 0;
    for (let i = 0; i < fieldsSignature.length; i++) {
      const char = fieldsSignature.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash; // Convert to 32bit integer
    }
    const shortHash = Math.abs(hash).toString(16).substring(0, 8);
    console.log('[AI Copilot] generateSafeBadges called, hash:', shortHash);
    
    // Check persistent cache first
    const cachedResult = this.safeBadgesCache.get(shortHash);
    if (cachedResult) {
      console.log('[AI Copilot] ✅ Using CACHED safe badges result');
      return cachedResult;
    }
    
    // If same request is already pending, wait for it
    if (this.pendingGenerateSafeBadges && this.lastProcessedFieldsHash === fieldsSignature) {
      console.log('[AI Copilot] 🔄 Joining existing generateSafeBadges request');
      return await this.pendingGenerateSafeBadges;
    }
    
    // Create new request
    console.log('[AI Copilot] 🆕 Creating NEW generateSafeBadges request (API calls)');
    this.lastProcessedFieldsHash = fieldsSignature;
    this.pendingGenerateSafeBadges = this.performGenerateSafeBadges(fieldsForDisplay);
    
    try {
      const result = await this.pendingGenerateSafeBadges;
      
      // Cache the result permanently
      this.safeBadgesCache.set(shortHash, result);
      console.log('[AI Copilot] 💾 Cached safe badges result for future use');
      
      return result;
    } finally {
      // Clear pending request after completion
      this.pendingGenerateSafeBadges = null;
    }
  }

  private async performGenerateSafeBadges(fieldsForDisplay: any[]) {
    const sensitiveFlags = await this.translationService.detectSensitiveFields(fieldsForDisplay);
    
    // Фильтруем только безопасные поля
    const safeFieldsForDisplay = fieldsForDisplay.filter((_field, index) => !sensitiveFlags[index]);
    
    // Дополнительная фильтрация: показываем бэджи только для полей, которые можно заполнить
    const fillableFields = safeFieldsForDisplay.filter(field => this.canFieldBeFilled(field));
    console.log(`[AI Copilot] 🎯 BADGE GENERATION: ${fillableFields.length}/${fieldsForDisplay.length} fillable fields | User: ${this.currentLang} | Form: ${this.detectedLanguage}`);

    const smartBadges = await this.translationService.generateSmartBadges(
        fillableFields,
        this.currentLang  
    );

    const translatedFields = await Promise.all(
        fillableFields.map(async (f, i) => {
          const badge = smartBadges[i];
          const original = f.label || f.placeholder || f.name || '';
          const sameWord = badge?.trim().toLowerCase() === original.trim().toLowerCase();
          const needsFallback = sameWord && this.detectedLanguage !== this.currentLang;

          const finalText = needsFallback
              ? await this.translationService.smartTranslate(original, this.currentLang)
              : (badge || original || 'Field');

          return { ...f, translatedText: finalText };
        })
    );

    return {
      safeFields: fillableFields,
      displayFields: translatedFields
    };
  }

  /**
   * Проверяет, может ли поле быть заполнено виджетом
   * Возвращает true только если есть работающий способ найти и заполнить поле
   */
  private canFieldBeFilled(field: any): boolean {
    const name = field.name;
    
    // 1. Если есть сохраненная ссылка на элемент - поле заполняемо
    if (field.element || this.fieldElementsMap.has(name)) {
      return true;
    }
    
    // 2. Проверяем стандартные способы поиска поля
    const forms = Array.from(document.forms);
    for (const form of forms) {
      // Проверяем стандартные селекторы
      if (form.elements.namedItem(name) || 
          form.querySelector(`[name="${name}"]`) ||
          form.querySelector(`[id="${name}"]`) ||
          form.querySelector(`[formcontrolname="${name}"]`)) {
        return true;
      }
    }
    
    // 3. Если поле не найдено ни одним способом - не показываем бэдж
    console.log(`[AI Copilot] ⚠️ Field "${name}" cannot be filled - no accessible DOM element found`);
    return false;
  }

  private applyValuesToSingleForm(values: { [key: string]: any }, form: HTMLFormElement) {
    console.log('[AI Copilot] Applying values to single form:', values);
    console.log('[AI Copilot] Form elements available:', Array.from(form.elements).map(el => ({
      name: (el as HTMLInputElement).name || el.id,
      type: (el as HTMLInputElement).type || el.tagName,
      id: el.id
    })));
    
    Object.entries(values).forEach(([name, value]) => {
      console.log(`[AI Copilot] Trying to fill field "${name}" with value:`, value);
      
      // ГЛАВНАЯ ПРОВЕРКА: заполняем только поля, которые отображаются в бэджах
      if (!this.safeFieldNames.includes(name)) {
        console.log(`[AI Copilot] Skipping field "${name}" - not in safe badge list. Safe fields:`, this.safeFieldNames);
        return;
      }
      
      let element = form.elements.namedItem(name);
      console.log(`[AI Copilot] Step 1 - namedItem("${name}"):`, element ? 'Found' : 'Not found');
      // 1b) Поиск по data-атрибуту (для полей, где мы не хотим светить name/id наружу)
      if (!element) {
        element = form.querySelector(`[data-copilot-key="${name}"]`) as HTMLElement;
        console.log(`[AI Copilot] Step 1b - data-copilot-key("${name}"):`, element ? 'Found' : 'Not found');
      }
      // Check fallback field mapping for fields without name/id attributes
      if (!element && this.fieldElementsMap.has(name)) {
        element = this.fieldElementsMap.get(name)!;
        console.log(`[AI Copilot] Step 2 - fallback mapping("${name}"):`, element ? 'Found' : 'Not found');
      }
      
      // Если не найдено по name, пробуем найти по id (с безопасным CSS селектором)
      if (!element) {
        try {
          // Проверяем, является ли name валидным CSS селектором
          if (/^[a-zA-Z_-]/.test(name)) {
            element = form.querySelector(`#${name}`) as HTMLElement;
            console.log(`[AI Copilot] Step 3 - querySelector("#${name}"):`, element ? 'Found' : 'Not found');
          } else {
            console.log(`[AI Copilot] Step 3 - skipping invalid CSS selector("#${name}")`);
          }
        } catch (e) {
          console.log(`[AI Copilot] Step 3 - invalid selector("#${name}"):`, e);
        }
      }
      
      // Специальный поиск для полей времени (time0000 -> placeholder="00:00")
      if (!element && name.startsWith('time') && /^time\d+$/.test(name)) {
        const timeValue = name.substring(4); // Убираем "time"
        const timePattern = timeValue.replace(/(\d{2})(\d{2})/, '$1:$2'); // 0000 -> 00:00
        element = form.querySelector(`[placeholder="${timePattern}"]`) as HTMLElement;
        console.log(`[AI Copilot] Step 4a - time field search ("${timePattern}"):`, element ? 'Found' : 'Not found');
      }
      
      // Поиск по formcontrolname (Angular reactive forms)
      if (!element) {
        element = form.querySelector(`[formcontrolname="${name}"]`) as HTMLElement;
        console.log(`[AI Copilot] Step 4 - formcontrolname("${name}"):`, element ? 'Found' : 'Not found');
      }
      
      // Поиск по data-testid и другим data-атрибутам
      if (!element) {
        element = form.querySelector(`[data-testid="${name}"], [data-cy="${name}"], [data-field="${name}"]`) as HTMLElement;
        console.log(`[AI Copilot] Step 5 - data attributes("${name}"):`, element ? 'Found' : 'Not found');
      }
      
      // Поиск по aria-labelledby
      if (!element) {
        element = form.querySelector(`[aria-labelledby="${name}"]`) as HTMLElement;
        console.log(`[AI Copilot] Step 6 - aria-labelledby("${name}"):`, element ? 'Found' : 'Not found');
      }
      
      // Если не найдено по id, пробуем найти по частичному совпадению
      if (!element) {
        const nameLower = name.toLowerCase();
        element = form.querySelector(`[name*="${nameLower}"], [id*="${nameLower}"]`) as HTMLElement;
        console.log(`[AI Copilot] Step 7 - partial match ("${nameLower}"):`, element ? 'Found' : 'Not found');
      }
      
      if (!element) {
        console.log(`[AI Copilot] Field "${name}" not found in active form (tried name, id, partial match)`);
        // Дополнительная диагностика - покажем все доступные поля
        console.log('[AI Copilot] Available form fields for debugging:');
        Array.from(form.elements).forEach((el, idx) => {
          console.log(`  [${idx}] name="${(el as HTMLInputElement).name}" id="${el.id}" type="${(el as HTMLInputElement).type || el.tagName}"`);
        });
        return;
      }

      // Пропускаем приватные поля
      if (element instanceof HTMLElement && element.dataset.aiPrivate === 'true') {
        console.log(`[AI Copilot] Skipping private field "${name}" - marked as data-ai-private`);
        return;
      }

      // Проверяем поля из метаданных формы как чувствительные
      const fieldMeta = this.scannedFields.find(f => f.name === name);
      if (fieldMeta?.isPrivate) {
        console.log(`[AI Copilot] Skipping sensitive field "${name}" - detected by field scanner`);
        return;
      }

      // Дополнительная защита: базовая проверка чувствительных полей
      if (element instanceof HTMLInputElement && element.type === 'password') {
        console.log(`[AI Copilot] Skipping password field "${name}"`);
        return;
      }

      console.log(`[AI Copilot] Filling field "${name}" with value:`, value);

      if (element instanceof RadioNodeList) {
        // Группа радиокнопок или чекбоксов с одинаковым именем
        Array.from(element).forEach(radioElement => {
          if (radioElement instanceof HTMLInputElement && radioElement.type === 'radio') {
            if (typeof value === 'string') {
              radioElement.checked = radioElement.value === value;
              if (radioElement.checked) {
                radioElement.dispatchEvent(new Event('input', { bubbles: true }));
                radioElement.dispatchEvent(new Event('change', { bubbles: true }));
              }
            }
          }
        });
      } else if (element instanceof HTMLInputElement && element.type === 'checkbox') {
        if (typeof value === 'boolean') {
          element.checked = value;
        } else if (typeof value === 'string') {
          element.checked = element.value === value || (value === 'on' && element.value === 'on');
        } else if (Array.isArray(value)) {
          element.checked = value.includes(element.value);
        }
        element.dispatchEvent(new Event('input', { bubbles: true }));
        element.dispatchEvent(new Event('change', { bubbles: true }));
      } else if (element instanceof HTMLElement) {
        // Для select, textarea, input[type=text], etc.
        if (typeof value === 'string' || typeof value === 'number') {
          let finalValue = String(value);
          
          (element as HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement).value = finalValue;
          element.dispatchEvent(new Event('input', { bubbles: true }));
          element.dispatchEvent(new Event('change', { bubbles: true }));
        } else if (value && typeof value === 'object' && 'formats' in value) {
          // Multi-format value handling для дат/времени
          const multiFormatValue = value as MultiFormatValue;
          const finalValue = this.selectBestDateTimeFormat(element, multiFormatValue);
          
          console.log(`[AI Copilot] Multi-format value processing for field "${name}":`, {
            elementType: (element as HTMLInputElement).type || element.tagName,
            tagName: element.tagName.toLowerCase(),
            selectedFormat: finalValue,
            availableFormats: multiFormatValue.formats
          });
          
          (element as HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement).value = finalValue;
          element.dispatchEvent(new Event('input', { bubbles: true }));
          element.dispatchEvent(new Event('change', { bubbles: true }));
        }
      }
    });
  }

  /**
   * Выбирает наилучший формат для даты/времени на основе типа элемента и его tagName
   */
  private selectBestDateTimeFormat(element: HTMLElement, multiFormatValue: MultiFormatValue): string {
    const elementType = (element as HTMLInputElement).type;
    const tagName = element.tagName.toLowerCase();
    const formats = multiFormatValue.formats;
    
    // Приоритет для ion-datetime и других Ionic компонентов
    if (tagName === 'ion-datetime' || tagName.includes('ion-')) {
      if (formats.iso) {
        console.log(`[AI Copilot] Selected ISO format for ${tagName}:`, formats.iso);
        return formats.iso;
      }
    }
    
    // Приоритет для HTML5 input types
    if (elementType === 'datetime-local' || elementType === 'date' || elementType === 'time') {
      if (formats.iso) {
        console.log(`[AI Copilot] Selected ISO format for ${elementType}:`, formats.iso);
        return formats.iso;
      }
    }
    
    // Приоритет для кастомных компонентов с числовыми значениями
    if (tagName.includes('timestamp') || tagName.includes('epoch')) {
      if (formats.timestamp) {
        console.log(`[AI Copilot] Selected timestamp format for ${tagName}:`, formats.timestamp);
        return String(formats.timestamp);
      }
    }
    
    // Приоритет для полей с числовыми форматами
    if (elementType === 'number' || tagName.includes('numeric')) {
      if (formats.numeric) {
        console.log(`[AI Copilot] Selected numeric format for ${elementType}/${tagName}:`, formats.numeric);
        return formats.numeric;
      }
    }
    
    // Для всех остальных случаев используем display format как наиболее читаемый
    if (formats.display) {
      console.log(`[AI Copilot] Selected display format for ${elementType}/${tagName}:`, formats.display);
      return formats.display;
    }
    
    // Fallback на ISO, если display недоступен
    if (formats.iso) {
      console.log(`[AI Copilot] Fallback to ISO format for ${elementType}/${tagName}:`, formats.iso);
      return formats.iso;
    }
    
    // Последний fallback на оригинальный текст
    console.log(`[AI Copilot] Fallback to original text for ${elementType}/${tagName}:`, multiFormatValue.originalText);
    return multiFormatValue.originalText;
  }

  private renderFormInfoText() {
    const activeForm = this.selectActiveForm();
    const totalForms = this.getTotalFormsCount();
    
    if (!activeForm && totalForms <= 1) {
      return '';
    }
    
    let formTitle = '';
    let formIndex = 1;
    let stats = { filled: 0, partiallyFilled: 0, empty: 0 };
    let progressInfo = '';

    if (activeForm) {
      formTitle = this.getFormTitle(activeForm);
      formIndex = this.getFormIndex(activeForm);
      stats = this.getFormsStatistics();
      
      if (stats.filled > 0 || stats.partiallyFilled > 0) {
        const completedText = stats.filled > 0 ? this.t('form.progress.filled', { count: stats.filled }) : '';
        const partialText = stats.partiallyFilled > 0 ? this.t('form.progress.partial', { count: stats.partiallyFilled }) : '';
        const parts = [completedText, partialText].filter(Boolean);
        progressInfo = parts.join(', ');
      }
    } else {
      stats = this.getFormsStatistics();
      if (stats.filled > 0 || stats.partiallyFilled > 0) {
        const completedText = stats.filled > 0 ? this.t('form.progress.filled', { count: stats.filled }) : '';
        const partialText = stats.partiallyFilled > 0 ? this.t('form.progress.partial', { count: stats.partiallyFilled }) : '';
        const parts = [completedText, partialText].filter(Boolean);
        progressInfo = parts.join(', ');
      }
      formTitle = this.t('form.allForms', { count: totalForms });
    }

    const metaInfo = [
      totalForms > 1 ? this.t('form.formNumber', { current: formIndex, total: totalForms }) : '',
      progressInfo,
      !activeForm && totalForms > 1 ? this.t('form.progress.scrollToSelect') : ''
    ].filter(Boolean).join(' • ');

    return html`
      <div class="form-info-text">
        <h3 class="form-info-title-text">${formTitle}</h3>
        ${metaInfo ? html`<p class="form-info-meta-text">${metaInfo}</p>` : ''}
      </div>
    `;
  }

  private renderInitialStep() {
    if (this.isLoadingBadges) {
      return html`
        <div class="processing-content">
          <div class="processing-icon">
            <svg width="28" height="28" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 10V3L4 14h7v7l9-11h-7z"></path>
            </svg>
          </div>
          <div class="processing-text">${this.t('processing.title')}</div>
          <div class="processing-subtitle">${this.t('processing.analyzingForm')}</div>
        </div>
      `;
    }

    const { safeFields, displayFields } = this.cachedSafeBadges || { safeFields: [], displayFields: [] };
    const shouldScroll = safeFields.length > 10;

    return html`
      <div class="modal-header-compact-modern">
        <div class="modal-header-content">
          <div class="modal-icon-compact-modern">
            <div class="icon-glow-subtle"></div>
            ${unsafeHTML(icons.mic)}
          </div>
          <div class="modal-text-group">
            <h2 class="modal-title-compact">${this.t('modal.title')}</h2>
            <p class="modal-subtitle-compact">${this.t('modal.subtitle')}</p>
          </div>
          <button class="close-button-modern" @click=${this.closeModal}>
            ${unsafeHTML(icons.close)}
          </button>
        </div>
        <div class="header-decoration"></div>
      </div>

      <div class="modal-body">
        ${this.renderFormInfoText()}
        
        <div class="field-badges-container-modern">
          ${safeFields.length > 0 ? html`
            <div class="field-badges-modern ${shouldScroll ? 'scrollable' : ''}">
              ${displayFields.map((field: any) => html`
                <span class="field-badge-modern">
                  <span class="badge-text">${field.translatedText || field.label || field.placeholder || field.name}</span>
                </span>
              `)}
            </div>
          ` : ''}
        </div>
      </div>

      <div class="modal-footer-modern">
        <button
            class="button-modern button-primary-modern"
            @click=${this.handleStartListening}
            ?disabled=${this.isLoading}
        >
          <div class="button-glow"></div>
          <div class="button-content">
            ${this._isRecording ? unsafeHTML(icons.stop) : unsafeHTML(icons.mic)}
            <span>${this._isRecording ? this.t('button.stopSpeaking') : this.t('button.startSpeaking')}</span>
          </div>
        </button>
      </div>
    `;
  }

  private renderListeningStep() {
    const { safeFields, displayFields } = this.cachedSafeBadges || { safeFields: [], displayFields: [] };
    const shouldScroll = safeFields.length > 10;

    return html`
      <div class="modal-header-compact-modern listening-header">
        <div class="modal-header-content">
          <div class="modal-icon-compact-modern listening-icon">
            <div class="icon-glow-subtle listening-glow"></div>
            ${unsafeHTML(icons.mic)}
          </div>
          <div class="modal-text-group">
            <h2 class="modal-title-compact">${this.t('listening.title')}</h2>
            <p class="modal-subtitle-compact">${this.t('listening.subtitle')}</p>
          </div>
          <button class="close-button-modern" @click=${this.closeModal}>
            ${unsafeHTML(icons.close)}
          </button>
        </div>
        <div class="header-decoration listening-decoration"></div>
      </div>

      <div class="modal-body">
        ${this.renderFormInfoText()}

        <div class="field-badges-container-modern">
          ${safeFields.length > 0 ? html`
            <p class="field-badges-label">${this.t('listening.fieldsToFill')}:</p>
            <div class="field-badges-modern ${shouldScroll ? 'scrollable' : ''}">
              ${displayFields.map((field: any) => html`
                <span class="field-badge-modern listening-badge">
                  <span class="badge-text">${field.translatedText || field.label || field.placeholder || field.name}</span>
                </span>
              `)}
            </div>
          ` : ''}
        </div>
      </div>

      <div class="modal-footer-modern">
        <button class="button-modern button-danger-modern" @click=${this.handleStopListening}>
          <div class="button-glow danger-glow"></div>
          <div class="button-content">
            ${unsafeHTML(icons.stop)}
            <span>${this.t('button.stopSpeaking')}</span>
          </div>
        </button>
      </div>
    `;
  }

  private renderProcessingStep() {
    return html`
      <div class="processing-content">
        <div class="processing-icon">
          <svg width="28" height="28" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 10V3L4 14h7v7l9-11h-7z"></path>
          </svg>
        </div>
        <div class="processing-text">${this.t('processing.title')}</div>
        <div class="processing-subtitle">${this.t('processing.subtitle')}</div>
      </div>
    `;
  }

  private getFieldCurrentValue(fieldName: string, form: HTMLFormElement | null): string {
    if (!form) return '';
    
    try {
      // Попробуем найти элемент по имени
      let element = form.elements.namedItem(fieldName) as HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement | null;
      
      // Если не нашли по имени, попробуем по id
      if (!element) {
        element = form.querySelector(`#${CSS.escape(fieldName)}`) as HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement | null;
      }
      
      // Если не нашли по id, попробуем по data-copilot-key (для полей без name/id)
      if (!element) {
        element = form.querySelector(`[data-copilot-key="${CSS.escape(fieldName)}"]`) as HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement | null;
      }
      
      // Если не нашли в форме, попробуем в fieldElementsMap
      if (!element && this.fieldElementsMap.has(fieldName)) {
        const mappedElement = this.fieldElementsMap.get(fieldName);
        if (mappedElement && form.contains(mappedElement)) {
          element = mappedElement as HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement;
        }
      }
      
      if (element) {
        // Обработка разных типов элементов
        if (element.tagName === 'SELECT') {
          return (element as HTMLSelectElement).value || '';
        } else if (element.type === 'checkbox') {
          return (element as HTMLInputElement).checked ? 'checked' : '';
        } else if (element.type === 'radio') {
          // Для радиокнопок нужно найти выбранную в группе
          const radioGroup = form.querySelectorAll(`input[type="radio"][name="${CSS.escape(fieldName)}"]`) as NodeListOf<HTMLInputElement>;
          for (const radio of radioGroup) {
            if (radio.checked) {
              return radio.value || 'checked';
            }
          }
          return '';
        } else {
          return element.value || '';
        }
      }
      
      return '';
    } catch (error) {
      console.error(`[AI Copilot] Error getting field value for "${fieldName}":`, error);
      return '';
    }
  }

  private getFieldStatuses() {
    if (!this.lastLLMResponse?.autofill || !this.scannedFields) {
      return { filled: [], needsCheck: [], empty: [] };
    }
    
    // 🛡️ УЛУЧШЕННАЯ ПРОВЕРКА КЭША: учитываем originalUserText
    const cacheKey = `${JSON.stringify(this.lastLLMResponse?.autofill || {})}_${JSON.stringify(this.originalUserText || {})}`;
    if (this._cachedFieldStatuses && this._lastLLMResponseForCache === cacheKey) {
      return this._cachedFieldStatuses;
    }
    
    const filledFields = this.lastLLMResponse.autofill;
    const safeFields = this.safeFieldNames || [];
    
    // Получаем переведенные названия полей из кеша
    let cachedDisplayFields: any[] = [];
    if (this.cachedSafeBadges && this.cachedSafeBadges.displayFields) {
      cachedDisplayFields = this.cachedSafeBadges.displayFields;
    }
    
    const filled: Array<{name: string, label: string, value: string}> = [];
    const needsCheck: Array<{name: string, label: string, value: string}> = [];
    const empty: Array<{name: string, label: string}> = [];
    
    // Получаем активную форму для проверки текущих значений
    const activeForm = this.selectActiveForm();
    
    this.scannedFields.forEach(field => {
      // Только безопасные поля
      if (!safeFields.includes(field.name)) return;
      
      const value = filledFields[field.name];
      const cachedField = cachedDisplayFields.find(f => f.name === field.name);
      const translatedLabel = cachedField?.translatedText;
      const label = translatedLabel || field.label || field.placeholder || field.name;
      
      // Проверяем текущее значение поля в DOM
      const currentFieldValue = this.getFieldCurrentValue(field.name, activeForm);
      
      if (!value || value === '') {
        // Если LLM не вернул значение, но поле уже заполнено в форме - НЕ показываем его как пустое
        if (currentFieldValue && currentFieldValue.trim() !== '') {
          console.log(`[AI Copilot] Field "${field.name}" already has value "${currentFieldValue}", skipping from empty list`);
          // Поле уже заполнено, пропускаем его
          return;
        }
        empty.push({ name: field.name, label });
      } else {
        // Проверяем, нужна ли дополнительная проверка
        const isEmailField = field.type === 'email' || 
                           field.name.toLowerCase().includes('email') || 
                           field.name.toLowerCase().includes('mail');
        
        // ВРЕМЕННО: только email поля требуют проверки
        // const fieldConfidence = this.lastLLMResponse?.confidence?.[field.name];
        // const hasLowConfidence = fieldConfidence !== undefined && 
        //                        fieldConfidence < CONFIG.FIELD_CONFIDENCE.NEEDS_CHECK_THRESHOLD;
        
        // Только email поля требуют проверки
        if (isEmailField) {
          needsCheck.push({ name: field.name, label, value: value as string });
        } else {
          filled.push({ name: field.name, label, value: value as string });
        }
      }
    });
    
    // 🛡️ СОХРАНЯЕМ В КЭШ с улучшенным ключом
    this._cachedFieldStatuses = { filled, needsCheck, empty };
    this._lastLLMResponseForCache = cacheKey;
    
    return this._cachedFieldStatuses;
  }

  private renderSuccessStep() {
    const { filled, needsCheck, empty } = this.getFieldStatuses();
    const hasAnyFields = filled.length > 0 || needsCheck.length > 0 || empty.length > 0;
    
    return html`
      <div class="modal-header-compact-modern success-header">
        <div class="modal-header-content">
          <div class="modal-text-group">
            <h2 class="modal-title-compact">${this.t('success.title')}</h2>
            <p class="modal-subtitle-compact">${this.t('success.subtitle')}</p>
            <!-- Прогресс заполнения -->
            <div class="progress-integrated">
              <div class="progress-inline-container">
                <div class="progress-track-slim">
                  <div class="progress-fill-modern" style="width: ${(filled.length / (filled.length + needsCheck.length + empty.length)) * 100}%">
                    <div class="progress-glow"></div>
                  </div>
                </div>
                <span class="progress-value-inline">${Math.round((filled.length / (filled.length + needsCheck.length + empty.length)) * 100)}%</span>
              </div>
            </div>
          </div>
          <button class="close-button-modern" @click=${this.closeModal}>
            ${unsafeHTML(icons.close)}
          </button>
        </div>
        <div class="header-decoration success-decoration"></div>
      </div>

      <div class="modal-body">
        ${this.renderFormInfoText()}

      <div class="modal-body">
        ${hasAnyFields ? html`
          <!-- Статусы полей -->
          <div class="fields-accordion-container">
            <!-- Незаполненные поля -->
            ${empty.length > 0 ? html`
              <div class="status-accordion ${this.expandedSections.empty ? 'expanded' : ''}" data-status="error">
                <button class="status-accordion-header" 
                  @click=${() => this.toggleSection('empty')}
                  aria-expanded="${this.expandedSections.empty}">
                  
                  <div class="status-indicator error">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                      <circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="2"/>
                      <path d="M12 8V12M12 16H12.01" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
                    </svg>
                  </div>
                  
                  <div class="status-info">
                    <h4 class="status-title">${this.t('success.empty_fields')}</h4>
                    <p class="status-subtitle">${empty.length} ${this.t(empty.length === 1 ? 'success.field_single' : 'success.fields_multiple')} ${this.t('success.require_filling')}</p>
                  </div>
                  
                  <div class="status-expand-icon ${this.expandedSections.empty ? 'expanded' : ''}">
                    <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                      <path d="M6 8L10 12L14 8" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                    </svg>
                  </div>
                </button>
                
                <div class="status-accordion-content" aria-hidden="${!this.expandedSections.empty}">
                  <div class="fields-badges-container">
                    ${empty.map((field) => html`
                      <span class="field-badge empty" >
                        ${field.label}
                      </span>
                    `)}
                  </div>
                </div>
              </div>
            ` : ''}
            
            <!-- Поля, требующие проверки -->
            ${needsCheck.length > 0 ? html`
              <div class="status-accordion ${this.expandedSections.check ? 'expanded' : ''}" data-status="warning">
                <button class="status-accordion-header" 
                  @click=${() => this.toggleSection('check')}
                  aria-expanded="${this.expandedSections.check}">
                  
                  <div class="status-indicator warning">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                      <path d="M12 2L2 20H22L12 2Z" stroke="currentColor" stroke-width="2" stroke-linejoin="round"/>
                      <path d="M12 9V13M12 17H12.01" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
                    </svg>
                  </div>
                  
                  <div class="status-info">
                    <h4 class="status-title">${this.t('success.check_fields')}</h4>
                    <p class="status-subtitle">${needsCheck.length} ${this.t(needsCheck.length === 1 ? 'success.field_single' : 'success.fields_multiple')} ${this.t('success.require_attention')}</p>
                  </div>
                  
                  <div class="status-expand-icon ${this.expandedSections.check ? 'expanded' : ''}">
                    <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                      <path d="M6 8L10 12L14 8" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                    </svg>
                  </div>
                </button>
                
                <div class="status-accordion-content" aria-hidden="${!this.expandedSections.check}">
                  <div class="warning-fields-modern">
                    ${needsCheck.map((field, index) => {
                      const originalText = String(this.originalUserText[field.name] || field.value);
                      const valuesMatch = originalText === String(field.value);
                      const isNonLinguistic = this.isNonLinguisticValue(originalText, String(field.value));
                      const languagesMatch = this.currentLang === this.detectedLanguage;
                      const showTranslation = !languagesMatch && !valuesMatch && !isNonLinguistic && this.originalUserText[field.name];
                      
                      if (showTranslation) {
                        return html`
                          <div class="field-card-modern field-card-modern--warning field-card-modern--translated" style="--delay: ${index * 0.05}s">
                            <div class="field-header-modern">
                              <div class="field-icon-modern field-icon-modern--warning">
                                <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                                  <path d="M8 1L1 14H15L8 1Z" stroke="currentColor" stroke-width="1.5" stroke-linejoin="round"/>
                                  <path d="M8 6V9M8 12H8.01" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
                                </svg>
                              </div>
                              <h5 class="field-label-modern">${field.label}</h5>
                            </div>
                            <div class="translation-flow-modern">
                              <div class="translation-step-modern">
                                <span class="step-label-modern">${this.t('success.you_said')}</span>
                                <div class="step-value-modern">${originalText}</div>
                              </div>
                              <div class="translation-arrow-modern">
                                <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                                  <path d="M5 12H19M19 12L12 5M19 12L12 19" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" opacity="0.3"/>
                                </svg>
                              </div>
                              <div class="translation-step-modern translation-step-modern--final">
                                <span class="step-label-modern">${this.t('success.we_translated')}</span>
                                <div class="step-value-modern step-value-modern--highlighted">${field.value}</div>
                              </div>
                            </div>
                          </div>
                        `;
                      } else {
                        return html`
                          <div class="field-card-modern field-card-modern--warning" style="--delay: ${index * 0.05}s">
                            <div class="field-header-modern">
                              <div class="field-icon-modern field-icon-modern--warning">
                                <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                                  <path d="M8 1L1 14H15L8 1Z" stroke="currentColor" stroke-width="1.5" stroke-linejoin="round"/>
                                  <path d="M8 6V9M8 12H8.01" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
                                </svg>
                              </div>
                              <div class="field-content-modern">
                                <h5 class="field-label-modern">${field.label}</h5>
                                <div class="field-value-modern">${field.value}</div>
                              </div>
                            </div>
                          </div>
                        `;
                      }
                    })}
                  </div>
                </div>
              </div>
            ` : ''}
            
            <!-- Успешно заполненные поля -->
            ${filled.length > 0 ? html`
              <div class="status-accordion ${this.expandedSections.filled ? 'expanded' : ''}" data-status="success">
                <button class="status-accordion-header" 
                  @click=${() => this.toggleSection('filled')}
                  aria-expanded="${this.expandedSections.filled}">
                  
                  <div class="status-indicator success">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                      <circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="2"/>
                      <path d="M8 12L11 15L16 9" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                    </svg>
                  </div>
                  
                  <div class="status-info">
                    <h4 class="status-title">${this.t('success.filled_fields')}</h4>
                    <p class="status-subtitle">${filled.length} ${this.t(filled.length === 1 ? 'success.field_single' : 'success.fields_multiple')} ${this.t('success.successfully_filled')}</p>
                  </div>
                  
                  <div class="status-expand-icon ${this.expandedSections.filled ? 'expanded' : ''}">
                    <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                      <path d="M6 8L10 12L14 8" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                    </svg>
                  </div>
                </button>
                
                <div class="status-accordion-content" aria-hidden="${!this.expandedSections.filled}">
                  <div class="filled-fields-modern">
                    ${filled.map((field, index) => {
                      const originalText = String(this.originalUserText[field.name] || field.value);
                      const valuesMatch = originalText === String(field.value);
                      const isNonLinguistic = this.isNonLinguisticValue(originalText, String(field.value));
                      const languagesMatch = this.currentLang === this.detectedLanguage;
                      const showTranslation = !languagesMatch && !valuesMatch && !isNonLinguistic && this.originalUserText[field.name];
                      
                      // Debug для searchQuery
                      if (field.name === 'searchQuery') {
                        console.log('[DEBUG] searchQuery translation check:', {
                          fieldName: field.name,
                          originalText,
                          fieldValue: field.value,
                          valuesMatch,
                          isNonLinguistic,
                          languagesMatch,
                          hasOriginalText: !!this.originalUserText[field.name],
                          showTranslation,
                          currentLang: this.currentLang,
                          detectedLanguage: this.detectedLanguage
                        });
                      }
                      
                      if (showTranslation) {
                        return html`
                          <div class="field-card-modern field-card-modern--translated" style="--delay: ${index * 0.05}s">
                            <div class="field-header-modern">
                              <div class="field-icon-modern">
                                <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                                  <path d="M2 8L6 12L14 4" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                                </svg>
                              </div>
                              <h5 class="field-label-modern">${field.label}</h5>
                            </div>
                            <div class="translation-flow-modern">
                              <div class="translation-step-modern">
                                <span class="step-label-modern">${this.t('success.you_said')}</span>
                                <div class="step-value-modern">${originalText}</div>
                              </div>
                              <div class="translation-arrow-modern">
                                <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                                  <path d="M5 12H19M19 12L12 5M19 12L12 19" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" opacity="0.3"/>
                                </svg>
                              </div>
                              <div class="translation-step-modern translation-step-modern--final">
                                <span class="step-label-modern">${this.t('success.we_translated')}</span>
                                <div class="step-value-modern step-value-modern--highlighted">${field.value}</div>
                              </div>
                            </div>
                          </div>
                        `;
                      } else {
                        return html`
                          <div class="field-card-modern" style="--delay: ${index * 0.05}s">
                            <div class="field-header-modern">
                              <div class="field-icon-modern">
                                <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                                  <path d="M2 8L6 12L14 4" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                                </svg>
                              </div>
                              <div class="field-content-modern">
                                <h5 class="field-label-modern">${field.label}</h5>
                                <div class="field-value-modern">${field.value}</div>
                              </div>
                            </div>
                          </div>
                        `;
                      }
                    })}
                  </div>
                </div>
              </div>
            ` : ''}
          </div>
        ` : ''}
      </div>

      <div class="modal-footer-modern success-footer">
        <button 
          class="button-modern button-primary-modern button-success-apply"
          @click="${this.handleFillForm}">
          <div class="button-content">
            <span>${this.t('success.apply_changes')}</span>
          </div>
        </button>
        
        <button 
          class="button-modern button-secondary-modern"
          @click="${this.closeModal}">
          <div class="button-content">
            <span>${this.t('button.close')}</span>
          </div>
        </button>
      </div>
    `;
  }

  private toggleSection(section: 'empty' | 'check' | 'filled') {
    // Мгновенное обновление без задержек
    this.expandedSections[section] = !this.expandedSections[section];
    this.requestUpdate('expandedSections');
  }
  
  // Методы для тултипов удалены после перехода на аккордеоны



  private renderErrorStep() {
    // Определяем специфичный заголовок для разных типов ошибок
    const isNoFieldsError = this.errorMessage?.includes(this.t('error.no_fields_detected'));
    const errorTitle = isNoFieldsError 
      ? this.t('error.no_data_title') 
      : this.t('error.general');
      
    return html`
      <div class="error-content">
        <div class="error-icon">
          ${unsafeHTML(icons.close)}
        </div>
        <h2 class="error-title">${errorTitle}</h2>
        <p class="error-message">${this.errorMessage}</p>

        ${this.errorDetails ? html`
          <details class="error-details">
            <summary class="error-details-summary">${this.t('error.showDetails')}</summary>
            <pre class="error-details-content">${this.errorDetails}</pre>
          </details>
        ` : ''}

        <button class="button button-primary button-full" @click=${this.resetToInitial}>
          ${this.t('button.tryAgain')}
        </button>
      </div>
    `;
  }

  render() {
    console.log(`[AI Copilot] Render called - hideWidget: ${this.hideWidget}`);
    if (this.hideWidget) {
      console.log('[AI Copilot] Widget hidden, returning empty template');
      return html``;
    }

    // Применяем динамические стили для позиции и z-index
    const dynamicStyles = `
      --widget-bottom: ${this.widgetPosition.bottom}px;
      --widget-right: ${this.widgetPosition.right !== undefined ? this.widgetPosition.right + 'px' : 'auto'};
      --widget-left: ${this.widgetPosition.left !== undefined ? this.widgetPosition.left + 'px' : 'auto'};
      --widget-z-index: ${this.smartZIndex.widget};
      --modal-z-index: ${this.smartZIndex.modal};
    `;

    return html`
      <div style="${dynamicStyles}">
      <div class="ai-form-copilot-widget ${this.showModal ? 'hidden' : ''}" @click=${this.handleFloatingButtonClick}>
        <div class="voice-widget-container">
          <div class="voice-widget-inner">
            <div class="voice-wave-container">
              <div class="voice-wave">
                <div class="wave-bar"></div>
                <div class="wave-bar"></div>
                <div class="wave-bar"></div>
                <div class="wave-bar"></div>
                <div class="wave-bar"></div>
              </div>
            </div>

            <div class="voice-widget-content">
              <div class="voice-widget-brand">AI Form Copilot</div>
              <div class="voice-widget-tagline">${this.t('floatingButton.fillVoice1')} • ${this.t('floatingButton.fillVoice2')}</div>
            </div>
          </div>
        </div>
      </div>

      <div class="modal-overlay ${this.showModal ? 'active' : ''}" @click=${(e: Event) => {
        if ((e.target as HTMLElement).classList.contains('modal-overlay')) this.closeModal();
      }}>
        <div class="modal-content ${this.currentStep === 'success' ? 'modal-content--success' : ''}" role="dialog">
          ${this.currentStep === 'error' ? html`
            <button class="close-button" @click=${this.closeModal}>
              ${unsafeHTML(icons.close)}
            </button>
          ` : ''}

          ${cache(this.currentStep === 'initial' ? this.renderInitialStep() : 
                  this.currentStep === 'listening' ? this.renderListeningStep() : 
                  this.currentStep === 'processing' ? this.renderProcessingStep() : 
                  this.currentStep === 'success' ? this.renderSuccessStep() : 
                  this.currentStep === 'error' ? this.renderErrorStep() : html``)}
        </div>
      </div>
      </div>
    `;
  }
}